/**
 * HeaderShadowFade
 *
 * Thin top-aligned LinearGradient overlay used by the calendar views to
 * soften the transition between the rounded-bottom header shadow and the
 * scrollable content below. As content scrolls up underneath, it appears
 * to fade out into the page background instead of being abruptly cut by
 * the header's bottom edge. Mirrors the TabBarGradientBlur pattern used
 * at the bottom of the screen.
 *
 * Place this as the LAST child of a relatively-positioned wrapper that
 * contains the scrollable area, so it renders on top of the scrolling
 * content. `pointerEvents="none"` keeps taps passing through.
 */

import { LinearGradient } from 'expo-linear-gradient';
import { StyleSheet, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

type HeaderShadowFadeProps = {
  /** Height of the fade in pixels. Defaults to 16. */
  height?: number;
};

export function HeaderShadowFade({ height = 16 }: HeaderShadowFadeProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const bg = palette.background;

  return (
    <View style={[styles.container, { height }]} pointerEvents="none">
      <LinearGradient
        colors={[bg, `${bg}00`]}
        style={styles.gradient}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 4,
  },
  gradient: {
    flex: 1,
  },
});
